/**
 * PACS Image Deletion System - Modality-Specific Rules
 *
 * This module defines deletion rules that maintain diagnostic integrity
 * while providing flexibility for veterinary PACS workflows.
 *
 * All deletions are soft deletes with audit trails.
 */
/**
 * Feature flag to enable/disable individual image deletion.
 * When false, only study and series deletion are available.
 */
const ENABLE_IMAGE_DELETION = false;
// Modality-specific deletion rules
const MODALITY_DELETION_RULES = {
    // DX (Digital Radiography) & CR (Computed Radiography)
    // Typically 2-4 view studies where series deletion doesn't make sense
    DX: {
        modality: 'DX',
        allowSeriesDelete: false,
        allowImageDelete: 'always',
        checkSequenceIntegrity: false,
        description: 'Digital Radiography - single images per series',
    },
    CR: {
        modality: 'CR',
        allowSeriesDelete: false,
        allowImageDelete: 'always',
        checkSequenceIntegrity: false,
        description: 'Computed Radiography - single images per series',
    },
    // CT (Computed Tomography)
    // Allow series delete, conditional image delete (only from scouts)
    CT: {
        modality: 'CT',
        allowSeriesDelete: true,
        allowImageDelete: 'conditional',
        checkSequenceIntegrity: true,
        description: 'CT - protect volumetric diagnostic sequences',
    },
    // MR (Magnetic Resonance)
    // Allow series delete, conditional image delete (only from scouts)
    MR: {
        modality: 'MR',
        allowSeriesDelete: true,
        allowImageDelete: 'conditional',
        checkSequenceIntegrity: true,
        description: 'MRI - protect diagnostic sequences',
    },
    // US (Ultrasound)
    // Allow series delete, conditional image delete (block cineloops)
    US: {
        modality: 'US',
        allowSeriesDelete: true,
        allowImageDelete: 'conditional',
        checkSequenceIntegrity: true,
        description: 'Ultrasound - protect cineloop temporal sequences',
    },
    // XA (X-Ray Angiography/Fluoroscopy)
    // Allow series delete, conditional image delete (block runs/sequences)
    XA: {
        modality: 'XA',
        allowSeriesDelete: true,
        allowImageDelete: 'conditional',
        checkSequenceIntegrity: true,
        description: 'Angiography - protect cinematic sequences',
    },
    // MG (Mammography)
    // Rare in vet medicine, treat like DX/CR
    MG: {
        modality: 'MG',
        allowSeriesDelete: false,
        allowImageDelete: 'always',
        checkSequenceIntegrity: false,
        description: 'Mammography - single images per series',
    },
};
// Default rule for unknown modalities
const DEFAULT_DELETION_RULE = {
    modality: 'DEFAULT',
    allowSeriesDelete: true,
    allowImageDelete: 'conditional',
    checkSequenceIntegrity: true,
    description: 'Default - conservative approach for unknown modalities',
};
/**
 * Get deletion rules for a specific modality
 */
function getModalityRules(modality) {
    if (!modality)
        return DEFAULT_DELETION_RULE;
    const upperModality = modality.toUpperCase();
    return MODALITY_DELETION_RULES[upperModality] || DEFAULT_DELETION_RULE;
}
/**
 * Keywords that indicate a scout/localizer series
 */
const SCOUT_KEYWORDS = ['scout', 'topogram', 'surview', 'localizer', 'projection'];
/**
 * Classify a series type based on DICOM metadata
 */
function classifySeriesType(series) {
    const modality = series.modality?.toUpperCase();
    // Check for scout/localizer series (CT/MR)
    if (modality === 'CT' || modality === 'MR') {
        // Check ImageType tag for LOCALIZER
        if (series.imageType?.toUpperCase().includes('LOCALIZER')) {
            return 'scout';
        }
        // Check series description for scout keywords
        if (series.seriesDescription) {
            const descLower = series.seriesDescription.toLowerCase();
            if (SCOUT_KEYWORDS.some((keyword) => descLower.includes(keyword))) {
                return 'scout';
            }
        }
        // Scout series typically have 1-3 images, diagnostic have many more
        if (series.images.length <= 3) {
            return 'scout';
        }
        return 'diagnostic';
    }
    // Check for cineloop (US, XA)
    if (modality === 'US' || modality === 'XA') {
        // Check if any image has numberOfFrames > 1 (cineloop)
        const hasCineloopImages = series.images.some((img) => img.numberOfFrames != null && img.numberOfFrames > 1);
        if (hasCineloopImages) {
            return 'cineloop';
        }
        // Check for FrameIncrementPointer existence (series-level indicator)
        if (series.frameIncrementPointer) {
            return 'cineloop';
        }
        // Check CineRate existence (XA)
        if (series.cineRate && series.cineRate > 0) {
            return 'cineloop';
        }
        // If all images have numberOfFrames=1, it's static
        return 'static';
    }
    // Default classification based on image count
    if (series.images.length > 1) {
        return 'diagnostic';
    }
    return 'static';
}
/**
 * Determine if individual image deletion is allowed for a series
 */
function canDeleteImagesFromSeries(series) {
    const modality = series.modality?.toUpperCase() || '';
    const rules = getModalityRules(modality);
    const seriesType = classifySeriesType(series);
    // Always allow
    if (rules.allowImageDelete === 'always') {
        return { allowed: true, reason: null };
    }
    // Never allow
    if (rules.allowImageDelete === 'never') {
        return {
            allowed: false,
            reason: { key: 'delete.blocked.notAllowedForModality', params: { modality } },
        };
    }
    // For US/XA with per-image numberOfFrames data, allow series-level deletion
    // (individual image checks happen via canDeleteImage)
    if (modality === 'US' || modality === 'XA') {
        // Check if we have per-image numberOfFrames data
        const hasFrameData = series.images.some((img) => img.numberOfFrames != null);
        if (hasFrameData) {
            // At least one static image can be deleted
            const hasStaticImages = series.images.some((img) => img.numberOfFrames == null || img.numberOfFrames <= 1);
            if (hasStaticImages) {
                return { allowed: true, reason: null };
            }
            // All images are cineloops
            return {
                allowed: false,
                reason: { key: 'delete.blocked.cineloopTemporal', params: { modality } },
            };
        }
        // No frame data - fall through to seriesType check
    }
    // Conditional - based on series type
    switch (seriesType) {
        case 'scout':
            return { allowed: true, reason: null };
        case 'cineloop':
            return {
                allowed: false,
                reason: { key: 'delete.blocked.cineloopTemporal', params: { modality } },
            };
        case 'diagnostic':
            if (modality === 'CT' || modality === 'MR') {
                return {
                    allowed: false,
                    reason: { key: 'delete.blocked.volumetricGaps', params: { modality } },
                };
            }
            return { allowed: true, reason: null };
        case 'static':
            return { allowed: true, reason: null };
        default:
            // Conservative default for unknown
            if (series.images.length > 1) {
                return {
                    allowed: false,
                    reason: { key: 'delete.blocked.multiFrame' },
                };
            }
            return { allowed: true, reason: null };
    }
}
export function calculateDeletionOptions(modality, series, totalSeries, hasCineloopImages) {
    const rules = getModalityRules(modality);
    // Dedup reasons by a stable key+params signature; insertion order preserved.
    const blockedReasons = new Map();
    const addReason = (reason) => {
        blockedReasons.set(`${reason.key}|${JSON.stringify(reason.params ?? {})}`, reason);
    };
    const hasSeriesData = series.length > 0;
    const mod = modality?.toUpperCase() || '';
    // Delete study is always available
    const showDeleteStudy = true;
    // Series deletion based on modality rules and series count
    const showDeleteSeries = rules.allowSeriesDelete && totalSeries > 1;
    // Image deletion - check each series
    const seriesEligibleForImageDeletion = [];
    if (rules.allowImageDelete === 'always') {
        // All series are eligible
        seriesEligibleForImageDeletion.push(...series);
    }
    else if (rules.allowImageDelete === 'conditional') {
        // Check each series
        for (const s of series) {
            const { allowed, reason } = canDeleteImagesFromSeries(s);
            if (allowed) {
                seriesEligibleForImageDeletion.push(s);
            }
            else if (reason) {
                addReason(reason);
            }
        }
    }
    else {
        // Never - add reason
        addReason(modality
            ? { key: 'delete.blocked.unavailableForModality', params: { modality } }
            : { key: 'delete.blocked.unavailableForThisModality' });
    }
    // Feature flag check - if image deletion is disabled, don't show the option
    if (!ENABLE_IMAGE_DELETION) {
        return {
            showDeleteStudy,
            showDeleteSeries,
            showDeleteImages: false,
            seriesEligibleForImageDeletion: [],
            blockedReasons: [],
        };
    }
    // Show delete images option if:
    // 1. We have eligible series, OR
    // 2. Series data not loaded yet AND modality ALWAYS allows image deletion (DX, CR, MG), OR
    // 3. Series data not loaded yet AND modality is US/XA AND we know there are static images
    //    (hasCineloopImages=false means all images are static and deletable,
    //     hasCineloopImages=true means some images may still be static and deletable)
    let showDeleteImages = seriesEligibleForImageDeletion.length > 0;
    if (!hasSeriesData) {
        if (rules.allowImageDelete === 'always') {
            // DX, CR, MG - always allow
            showDeleteImages = true;
        }
        else if (rules.allowImageDelete === 'conditional' && (mod === 'US' || mod === 'XA')) {
            // For US/XA, show option if we have hasCineloopImages info
            // Even if hasCineloopImages is true, there may still be static images that can be deleted
            if (hasCineloopImages !== undefined) {
                showDeleteImages = true; // Will check per-image when series loads
            }
        }
    }
    return {
        showDeleteStudy,
        showDeleteSeries,
        showDeleteImages,
        seriesEligibleForImageDeletion,
        blockedReasons: [...blockedReasons.values()],
    };
}
